/* eslint-disable indent */
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useForm, useFormState } from 'react-hook-form';
import { Button } from 'primereact/button';
import { useMutation } from 'react-query';
import { Dialog } from 'primereact/dialog';

import { useNavigate } from 'react-router-dom';
import { toast } from 'layout';
import { sendMailMultipleProjects, sendMailRemindMultipleProjects } from 'apis/councils.api';
import Loading from 'components/Loading';
import ErrorDialog from 'components/ErrorDialog';
import { HugerteEditor, TextInput } from 'components/FormControl';
import { sendRemindCouncilValidationSchema } from 'features/Council/validation';
import { TIMEZONE_VN, TOAST } from 'constant';
import ConfirmDialog from 'components/ConfirmDialog';
import { catchError } from 'utils/func';

const formOptions = { resolver: yupResolver(sendRemindCouncilValidationSchema) };
export default function SendMailRemindMultipleProjectDialog({
  visible,
  setVisible,
  council,
  refetch,
}) {
  // #region Data
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutate, error, isError, isLoading } = useMutation(sendMailRemindMultipleProjects);
  const { mutateAsync: mutateSendEstablishedEmailToAllMemberAsync } =
    useMutation(sendMailMultipleProjects);

  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const [isVisibleConfirmDialog, setIsVisibleConfirmDialog] = useState(false);
  const [isSentEmailLoading, setIsSentEmailLoading] = useState(false);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange', ...formOptions });
  const { isDirty, dirtyFields } = useFormState({
    control,
  });
  // #endregion Data

  // #region Events
  const onSubmit = async (data) => {
    mutate(
      {
        councilId: council?.id,
        title: data?.title,
        content: data?.content,
      },
      {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('council.member.confirm.success'));
          setValue('content', '');
          setValue('title', '');
          setIsVisibleDialogError(false);
          refetch();
          setVisible(false);
        },
        onError: () => {
          setIsVisibleDialogError(true);
        },
      }
    );
  };
  const checkValidation = () => {
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };

  const handleSendEmailCouncilToMember = async () => {
    try {
      setIsSentEmailLoading(true);
      const res = await mutateSendEstablishedEmailToAllMemberAsync({
        councilId: council?.id,
        isSentMail: true,
        isForce: true,
      });
      setIsVisibleDialogError(false);

      if (typeof refetch === 'function') {
        refetch();
      }

      setVisible(false);
      setIsVisibleConfirmDialog(false);
      setTimeout(() => {
        setIsSentEmailLoading(false);
      }, 50);

      if (res.status === 200) {
        toast(TOAST.SUCCESS, t('council.viewList.sendEstablishCouncilEmailSuccess'));
      } else {
        toast(TOAST.SUCCESS, t('council.viewList.sendEmailToMemberFailed'));
      }
    } catch (sendMailError) {
      catchError(t, sendMailError, navigate);
      setIsSentEmailLoading(false);
    }
  };

  const onReject = () => {
    setIsVisibleConfirmDialog(false);
  };
  // #endregion Events

  return (
    <>
      {isLoading && <Loading />}
      {isError && (
        <ErrorDialog
          title={t('council.viewList.remindMembers.titleRemindMembersError')}
          isError={isError}
          error={error}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}

      <Dialog
        header={t('council.viewList.remindMembers.header')}
        visible={visible}
        onHide={() => setVisible(false)}
        draggable={false}
        className="w-full sm:w-8 lg:w-6"
      >
        <div className="flex ml-3 flex-column relative pr-8">
          <div className="mb-2">
            <span className="font-semibold">
              {t(`council.fields.name.${council?.councilType?.code}`)}:{' '}
            </span>
            <span>{council?.name}</span>
          </div>
          <div className="mb-2">
            <span className="font-semibold">{t('council.fields.numberSentMail')}: </span>
            <span>{council?.projectProposalCouncil?.numberSentMail ?? 0}</span>
          </div>
          <div className="mb-2">
            <span className="font-semibold">{t('council.fields.lastSentMail')}: </span>
            <span>
              {council?.projectProposalCouncil?.lastSentMail
                ? new Date(council?.projectProposalCouncil?.lastSentMail).toLocaleDateString('vi', {
                    hour: 'numeric',
                    minute: 'numeric',
                    second: 'numeric',
                    timeZone: TIMEZONE_VN,
                  })
                : ''}
            </span>
          </div>
          <Button
            className="bg-green-500 border-green-500 hover:bg-green-700 absolute right-0"
            style={{ top: '5px' }}
            icon="pi pi-envelope"
            severity="info"
            tooltip={t('council.review.sendEstablishedEmailToAllMembers')}
            tooltipOptions={{ position: 'left' }}
            onClick={() => {
              setIsVisibleConfirmDialog(true);
            }}
          />
        </div>
        <form className="p-fluid relative ml-3" onSubmit={handleSubmit(onSubmit)}>
          <div className="relative">
            <div>
              <TextInput
                label={t('council.viewList.remindMembers.title')}
                isRequired
                name="title"
                control={control}
                errors={errors}
              />
            </div>
            <div>
              <HugerteEditor
                label={t('council.viewList.remindMembers.content')}
                isRequired
                name="content"
                isFontWeightLabel
                height="200px"
                control={control}
                errors={errors}
              />
            </div>
            <div className="flex flex-wrap justify-content-end mt-8">
              <Button
                label={t('formLayout.action.close')}
                severity="danger"
                type="button"
                onClick={() => setVisible(false)}
                className="mr-2 w-8rem"
              />
              <Button
                label={t('formLayout.action.sendMail')}
                severity="info"
                type="submit"
                onClick={checkValidation}
                disabled={!(isDirty && Object.keys(dirtyFields).length)}
                className="w-8rem"
              />
            </div>
          </div>
        </form>
      </Dialog>

      <ConfirmDialog
        content={
          <p>
            {t('council.review.confirmToSendEmailToAllMembers')}{' '}
            <b>{t('council.review.confirmToSendEmailToAllMembersNote')}</b>
          </p>
        }
        visible={isVisibleConfirmDialog}
        setVisible={setIsVisibleConfirmDialog}
        onReject={onReject}
        onHide={onReject}
        onAccept={handleSendEmailCouncilToMember}
        loading={isLoading || isSentEmailLoading}
      />
    </>
  );
}

SendMailRemindMultipleProjectDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  refetch: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  council: PropTypes.object,
};

SendMailRemindMultipleProjectDialog.defaultProps = {
  council: {},
  refetch: () => {},
};
